import { cn } from '@/lib/utils/cn';

/**
 * Habillage commun des champs de formulaire.
 *
 * Extrait ici pour que `TextField`, `Select` et `Textarea` partagent
 * exactement la même bordure, le même reflet et le même anneau de focus. Trois
 * copies du même bloc de classes auraient divergé au premier ajustement —
 * c'est toujours ce qui arrive.
 *
 * Le violet n'apparaît qu'au focus : la charte le réserve à l'interaction,
 * « le violet doit attirer l'attention, pas dominer ».
 */
export const fieldShell = ({
  hasError = false,
  disabled = false,
}: { hasError?: boolean; disabled?: boolean } = {}): string =>
  cn(
    'group relative flex items-center rounded-lg border bg-navy-50/60 bg-linear-to-b from-white/80 to-transparent',
    // Transition ciblée plutôt que `transition-all` : n'anime que ce qui
    // change réellement, et n'introduit aucune animation de géométrie.
    'transition-[border-color,box-shadow] duration-200 ease-out motion-reduce:transition-none',
    hasError
      ? [
          'border-red-300',
          !disabled && 'hover:border-red-400',
          'focus-within:border-red-500 focus-within:ring-[3px] focus-within:ring-red-500/12',
        ]
      : [
          'border-navy-200/80',
          !disabled && 'hover:border-navy-300',
          'focus-within:border-accent-700 focus-within:ring-accent-700/12 focus-within:ring-[3px]',
        ],
    'shadow-[inset_0_1px_0_0_rgb(255_255_255/0.95),inset_0_-1px_0_0_rgb(15_42_95/0.03),0_2px_4px_-1px_rgb(15_42_95/0.08)]',
    !disabled &&
      'hover:shadow-[inset_0_1px_0_0_rgb(255_255_255/0.95),inset_0_-1px_0_0_rgb(15_42_95/0.03),0_3px_6px_-2px_rgb(15_42_95/0.12)]',
    disabled && 'bg-navy-50/50 bg-none opacity-60 shadow-none',
  );

/** Libellé au-dessus du champ. */
export const FIELD_LABEL = 'text-navy-900 block text-sm font-semibold';

/** Espacement entre le libellé, le champ et son message. */
export const FIELD_STACK = 'space-y-[clamp(0.25rem,0.9vh,0.5rem)]';

/**
 * Valeur saisie, quelle que soit la nature du champ.
 *
 * Curseur et sélection aux couleurs de marque : c'est le genre de détail qui
 * sépare un « champ par défaut » d'un champ dessiné.
 */
export const FIELD_INPUT =
  'min-w-0 w-full bg-transparent text-[15px] text-navy-900 outline-none placeholder:text-ink-500 ' +
  'caret-accent-700 selection:bg-accent-100 selection:text-accent-900 disabled:cursor-not-allowed';
